import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, X, Clock, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { getPendingApprovals, approveApproval, rejectApproval } from '@/lib/approvalApi';

/**
 * ApprovalQueue Component
 * 
 * Displays pending approval requests for hotel managers/owners.
 * Allows approve/reject actions with inline details.
 */
export const ApprovalQueue = ({ hotelId, userRole = 'manager', userId, userEmail }) => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  // Load pending approvals
  const loadApprovals = async () => {
    try {
      setLoading(true);
      const data = await getPendingApprovals(hotelId, userRole);
      setApprovals(data);
    } catch (error) {
      console.error('Failed to load approvals:', error);
      toast.error('Failed to load pending approvals', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApprovals();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(loadApprovals, 30000);
    return () => clearInterval(interval);
  }, [hotelId, userRole]);

  // Handle approve action
  const handleApprove = async (approval) => {
    if (!userId) {
      toast.error('User ID required', {
        description: 'Cannot approve without authenticated user'
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to approve this request?\n\n` +
      `Tool: ${approval.tool_name}\n` +
      `Requested by: ${approval.requested_by_user_id || 'Unknown'}\n\n` +
      `This will execute the action immediately.`
    );

    if (!confirmed) return;

    try {
      setProcessingId(approval.id);
      
      const result = await approveApproval(approval.id, {
        approver_id: userId,
        approver_role: userRole,
        approver_email: userEmail,
        comment: 'Approved via dashboard'
      });

      toast.success('Request approved!', {
        description: `${approval.tool_name} has been executed successfully`
      });

      // Reload approvals
      await loadApprovals();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Failed to approve request', {
        description: error.message
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Handle reject action
  const handleReject = async (approval) => {
    if (!userId) {
      toast.error('User ID required', {
        description: 'Cannot reject without authenticated user'
      });
      return;
    }

    const reason = window.prompt(
      `Why are you rejecting this request?\n\n` +
      `Tool: ${approval.tool_name}\n` +
      `Requested by: ${approval.requested_by_user_id || 'Unknown'}`
    );

    if (!reason || reason.trim() === '') return;

    try {
      setProcessingId(approval.id);
      
      await rejectApproval(approval.id, {
        approver_id: userId,
        approver_role: userRole,
        approver_email: userEmail,
        reason: reason.trim()
      });

      toast.success('Request rejected', {
        description: 'The request has been denied'
      });

      // Reload approvals
      await loadApprovals();
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject request', {
        description: error.message
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (hours) => {
    if (hours < 0) return <span className="text-destructive">Expired</span>;
    if (hours < 1) return <span className="text-warning">{Math.floor(hours * 60)} minutes left</span>;
    if (hours < 24) return <span>{Math.floor(hours)} hours left</span>;
    return <span>{Math.floor(hours / 24)} days left</span>;
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // Empty state
  if (approvals.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Clock className="h-12 w-12 mb-4 opacity-30" />
          <p className="text-lg font-medium mb-1">No pending approvals</p>
          <p className="text-sm text-muted-foreground">
            All agent requests have been reviewed
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold heading-font">Pending Approvals</h3>
          <Badge variant="destructive" className="font-mono">
            {approvals.length}
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadApprovals}
          disabled={loading}
          data-testid="refresh-approvals-btn"
        >
          Refresh
        </Button>
      </div>

      {approvals.map((approval) => {
        const isExpanded = expandedId === approval.id;
        const isProcessing = processingId === approval.id;

        return (
          <Card key={approval.id} className="overflow-hidden" data-testid={`approval-${approval.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="font-medium">
                      {approval.agent_name}
                    </Badge>
                    <Badge variant="secondary">
                      {approval.required_approval_level}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatTimeRemaining(approval.time_remaining_hours)}
                    </span>
                  </div>
                  
                  <CardTitle className="text-base mb-1 font-mono">
                    {approval.tool_name}
                  </CardTitle>
                  
                  <CardDescription className="text-sm">
                    {approval.request_reason}
                  </CardDescription>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-[hsl(var(--web3-primary))] hover:bg-[hsl(var(--web3-primary))]/90"
                    onClick={() => handleApprove(approval)}
                    disabled={isProcessing}
                    data-testid={`approve-${approval.id}`}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleReject(approval)}
                    disabled={isProcessing}
                    data-testid={`reject-${approval.id}`}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => setExpandedId(isExpanded ? null : approval.id)}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Hide details
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    Show details
                  </>
                )}
              </Button>

              {isExpanded && (
                <div className="mt-3 p-3 bg-muted/30 rounded-md space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-muted-foreground">Created:</span>{' '}
                    <span className="font-mono text-xs">
                      {new Date(approval.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Expires:</span>{' '}
                    <span className="font-mono text-xs">
                      {new Date(approval.expires_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground">Status:</span>{' '}
                    <Badge variant="outline">{approval.status}</Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default ApprovalQueue;
